import { computed } from "vue";
import { calculateCableOnSurface } from "@/libs/calculations/cables";
import { extrapolateTVDByMD } from "@/libs/calculations/deviationSurvey";
import { Inches } from "@/libs/units/length";
import {
  MLE_ADDITIONAL_LENGTH_FEET,
  MLE_RECOMMENDED_ADDITIONAL_LENGTH_FEET,
  BMB_LENGTH_SAGD_FEET,
  DEFAULT_CABLE_LENGTH_FEET
} from "@/libs/calculations/constants";
import type { LengthsAndDepthsCtx } from "./context";

// Equipment lengths, depths (MD and TVD), diameters and cross sections.
export const defineLengthsAndDepths = (ctx: LengthsAndDepthsCtx) => {
  const {
    _equipment,
    _wellProperties,
    orderedYToolBypassTubings,
    orderedYToolEquipmentTubings,
    orderedIntakes,
    descendingOrderPumps,
    descendingOrderSeals,
    currentCables,
    primarySeal
  } = ctx;

  // motorLengthFt (length of motor)
  const motorLengthFt = computed(() =>
    Inches.toStandardUnit(_equipment.value?.r_motor?.length_inches || 0)
  );

  // boltOnDischargeLengthFt (length of the bolt-on discharge)
  const boltOnDischargeLengthFt = computed(() =>
    Inches.toStandardUnit(
      _equipment.value?.r_bolt_on_discharge?.length_inches || 0
    )
  );

  // sensorLengthFt (length of sensor)
  const sensorLengthFt = computed(() =>
    Inches.toStandardUnit(_equipment.value?.r_sensor?.length_inches || 0)
  );

  // intakeLengthFt (add all intake lengths together)
  const intakeLengthFt = computed(() =>
    Inches.toStandardUnit(
      orderedIntakes.value.reduce(
        (sum, { r_intake }) => sum + r_intake?.length_inches,
        0
      )
    )
  );

  // sealLengthFt (add all seal lengths together)
  const sealLengthFt = computed(() =>
    Inches.toStandardUnit(
      descendingOrderSeals.value.reduce(
        (sum, { r_seal }) => sum + r_seal?.length_inches,
        0
      )
    )
  );

  // pumpLengthFt (for each pump, add each housing length together)
  const pumpLengthFt = computed(() =>
    descendingOrderPumps.value.reduce(
      (sum, pump) => sum + (pump.length_feet || 0),
      0
    )
  );

  // Minimum length MLE cable needs to reach
  // Add 5 ft buffer for motor head
  const lengthForMleFt = computed(
    () =>
      pumpLengthFt.value +
      intakeLengthFt.value +
      sealLengthFt.value +
      MLE_ADDITIONAL_LENGTH_FEET
  );

  // Recommended length MLE should reach that
  // has 15 ft buffer
  const recommendedMleLengthFt = computed(
    () =>
      pumpLengthFt.value +
      intakeLengthFt.value +
      sealLengthFt.value +
      MLE_RECOMMENDED_ADDITIONAL_LENGTH_FEET
  );

  const equipmentLengthSAGDFt = computed(
    () =>
      pumpLengthFt.value +
      intakeLengthFt.value +
      sealLengthFt.value +
      motorLengthFt.value +
      BMB_LENGTH_SAGD_FEET
  );

  const totalCableOnlyLengthFt = computed(() =>
    currentCables.value.reduce(
      (sum, cable) => sum + (cable.length_feet || 0),
      0
    )
  );

  // Includes all cable plus MLE
  const totalCableLengthFt = computed(
    () =>
      totalCableOnlyLengthFt.value + (_equipment.value?.r_mle?.length_feet || 0)
  );

  // Equipment depth offset for lower stack calculations
  // When calculating for lower stack, we need to account for the upper stack's
  // y-tool bypass tubing length as an offset for depth/length calculations
  const equipmentDepthOffsetFt = computed(() => {
    return 0;
    // No offset needed for upper stack calculations
    // if (upholeStack || !yToolData.value.useYTool) {
    //   return 0;
    // }

    // // For lower stack, calculate sum of upper stack y-tool bypass tubing lengths
    // const originalEquip = originalEquipment.value;
    // if (!originalEquip) {
    //   return 0;
    // }

    // // Only calculate offset if uphole y-tool is actually enabled
    // // This prevents incorrect offset calculation when the y-tool is disabled
    // // but relationship data might still exist
    // if (!originalEquip.use_uphole_y_tool) {
    //   return 0;
    // }

    // // Get order array from equipment
    // const order =
    //   (originalEquip.uphole_y_tool_bypass_tubing_order as
    //     | string[]
    //     | undefined) || [];

    // // Use reactive form data instead of relationship array
    // // This ensures we react to form updates when tubing lengths change
    // const allBypassTubings = toValue(yToolBypassTubings);

    // // Filter tubings by order and sum their lengths
    // // Exclude deleted tubings (those with journal_deleted_at set)
    // const orderedTubings = order
    //   .map(id => allBypassTubings[id])
    //   .filter(
    //     (tubing): tubing is NonNullable<typeof tubing> =>
    //       tubing != null && !tubing.journal_deleted_at
    //   );

    // return orderedTubings.reduce(
    //   (sum, tubing) => sum + (tubing.length_feet || 0),
    //   0
    // );
  });

  // These are based off of measured depth, not tvd
  // For lower stack calculations, add offset for upper stack y-tool bypass tubing
  const tubingLengthFt = computed(() => {
    const productionTubing =
      (_wellProperties.value?.top_tubing_length_feet || 0) +
      (_wellProperties.value?.bottom_tubing_length_feet || 0);
    return productionTubing + equipmentDepthOffsetFt.value;
  });

  const yToolLengthFt = computed(() => {
    if (
      _equipment.value?.use_uphole_y_tool &&
      _equipment.value?.r_uphole_y_tool
    ) {
      return Inches.toStandardUnit(
        _equipment.value?.r_uphole_y_tool.length_inches || 0
      );
    }
    return 0;
  });

  const bottomOfYToolDepthFt = computed(() => {
    if (
      _equipment.value?.use_uphole_y_tool &&
      _equipment.value?.r_uphole_y_tool
    ) {
      return tubingLengthFt.value + yToolLengthFt.value;
    }
    return 0;
  });

  const totalCasingLengthFt = computed(
    () =>
      (_wellProperties.value?.top_casing_length_feet || 0) +
      (_wellProperties.value?.bottom_casing_length_feet || 0)
  );

  const yToolEquipmentTubingLengthFt = computed(() => {
    if (_equipment.value?.use_uphole_y_tool) {
      return orderedYToolEquipmentTubings.value.reduce(
        (sum, tubing) => sum + (tubing.length_feet || 0),
        0
      );
    }
    return 0;
  });

  const yToolBypassTubingLengthFt = computed(() => {
    if (_equipment.value?.use_uphole_y_tool) {
      return orderedYToolBypassTubings.value.reduce(
        (sum, tubing) => sum + (tubing.length_feet || 0),
        0
      );
    }
    return 0;
  });

  const yToolBypassTubingDepthFt = computed(() => {
    return yToolBypassTubingLengthFt.value + bottomOfYToolDepthFt.value;
  });

  const yToolBypassTubingMaxODIn = computed(() => {
    if (!_equipment.value?.use_uphole_y_tool) {
      return 0;
    }

    // Find max OD across ordered, non-deleted tubings
    return orderedYToolBypassTubings.value.reduce((maxOd, tubing) => {
      const od = tubing?.r_tubing?.outer_diameter_inches || 0;
      return typeof od === "number" && od > maxOd ? od : maxOd;
    }, 0);
  });

  // The bolt-on discharge sits on top of the uppermost pump, so its top is
  // where the tubing ends. This is the depth completePumpDepthFt held before
  // the discharge existed.
  const boltOnDischargeDepthFt = computed<number>(() => {
    return (
      tubingLengthFt.value +
        yToolLengthFt.value +
        yToolEquipmentTubingLengthFt.value || ctx.basicData.value.datumPointFt
    );
  });

  // Top of the pumps, which is the bottom of the discharge.
  const completePumpDepthFt = computed<number>(() => {
    return boltOnDischargeDepthFt.value + boltOnDischargeLengthFt.value;
  });

  const bottomOfPumpsDepthFt = computed(
    () =>
      tubingLengthFt.value +
      yToolLengthFt.value +
      yToolEquipmentTubingLengthFt.value +
      boltOnDischargeLengthFt.value +
      pumpLengthFt.value
  );

  const bottomOfIntakesDepthFt = computed(
    () => bottomOfPumpsDepthFt.value + intakeLengthFt.value
  );

  const bottomOfTopSealDepthFt = computed(
    () =>
      bottomOfIntakesDepthFt.value +
      Inches.toStandardUnit(primarySeal.value?.r_seal?.length_inches)
  );

  const bottomOfSealsDepthFt = computed(
    () => bottomOfIntakesDepthFt.value + sealLengthFt.value
  );

  const bottomOfMotorDepthFt = computed(
    () => bottomOfSealsDepthFt.value + motorLengthFt.value
  );

  const bottomOfEquipmentDepthFt = computed(
    () => bottomOfMotorDepthFt.value + sensorLengthFt.value
  );

  const equipmentLengthFt = computed<number>(
    () =>
      motorLengthFt.value +
      sensorLengthFt.value +
      intakeLengthFt.value +
      sealLengthFt.value +
      boltOnDischargeLengthFt.value +
      pumpLengthFt.value
  );

  const shroudLengthFt = computed(() => {
    if (_equipment.value?.shroud_id) {
      return bottomOfEquipmentDepthFt.value - bottomOfPumpsDepthFt.value;
    }
    return 0;
  });

  const depthToReachMLEPlugFt = computed(() => {
    return (
      tubingLengthFt.value +
      yToolLengthFt.value +
      yToolEquipmentTubingLengthFt.value +
      lengthForMleFt.value
    );
  });

  const recommendedTotalCableLengthFt = computed(() => {
    return (
      depthToReachMLEPlugFt.value -
      (_equipment.value?.r_mle?.length_feet || 0) +
      DEFAULT_CABLE_LENGTH_FEET
    );
  });

  const cableSurfaceLengthFt = computed(() => {
    return calculateCableOnSurface(
      totalCableLengthFt.value,
      depthToReachMLEPlugFt.value
    );
  });

  const tubingsDepthTVDFt = computed(() => {
    return extrapolateTVDByMD(
      boltOnDischargeDepthFt.value,
      ctx.entryRows.value
    );
  });

  // Bottom of the discharge, which is the top of the pumps.
  const bottomOfDischargeDepthTVDFt = computed(() => {
    return extrapolateTVDByMD(completePumpDepthFt.value, ctx.entryRows.value);
  });

  const bottomOfPumpsTVDFt = computed(() => {
    return extrapolateTVDByMD(bottomOfPumpsDepthFt.value, ctx.entryRows.value);
  });

  const bottomOfIntakesTVDFt = computed(() => {
    return extrapolateTVDByMD(
      bottomOfIntakesDepthFt.value,
      ctx.entryRows.value
    );
  });

  const bottomOfSealsTVDFt = computed(() => {
    return extrapolateTVDByMD(bottomOfSealsDepthFt.value, ctx.entryRows.value);
  });

  const bottomOfMotorTVDFt = computed(() => {
    return extrapolateTVDByMD(bottomOfMotorDepthFt.value, ctx.entryRows.value);
  });

  const bottomOfEquipmentTVDFt = computed(() => {
    return extrapolateTVDByMD(
      bottomOfEquipmentDepthFt.value,
      ctx.entryRows.value
    );
  });

  return {
    motorLengthFt,
    boltOnDischargeLengthFt,
    sensorLengthFt,
    intakeLengthFt,
    sealLengthFt,
    pumpLengthFt,
    lengthForMleFt,
    recommendedMleLengthFt,
    equipmentLengthSAGDFt,
    totalCableOnlyLengthFt,
    totalCableLengthFt,
    equipmentDepthOffsetFt,
    tubingLengthFt,
    yToolLengthFt,
    bottomOfYToolDepthFt,
    totalCasingLengthFt,
    yToolEquipmentTubingLengthFt,
    yToolBypassTubingLengthFt,
    yToolBypassTubingDepthFt,
    yToolBypassTubingMaxODIn,
    boltOnDischargeDepthFt,
    completePumpDepthFt,
    bottomOfPumpsDepthFt,
    bottomOfIntakesDepthFt,
    bottomOfTopSealDepthFt,
    bottomOfSealsDepthFt,
    bottomOfMotorDepthFt,
    bottomOfEquipmentDepthFt,
    equipmentLengthFt,
    shroudLengthFt,
    depthToReachMLEPlugFt,
    recommendedTotalCableLengthFt,
    cableSurfaceLengthFt,
    tubingsDepthTVDFt,
    bottomOfDischargeDepthTVDFt,
    bottomOfPumpsTVDFt,
    bottomOfIntakesTVDFt,
    bottomOfSealsTVDFt,
    bottomOfMotorTVDFt,
    bottomOfEquipmentTVDFt
  };
};

export type LengthsAndDepthsCalcs = ReturnType<typeof defineLengthsAndDepths>;
